import { useNavigate, useParams } from "react-router-dom";
import { plansApi } from "../../services/plans/plansApi";
import { Button } from "primereact/button";
import { FormikProvider, useFormik } from "formik";
import { Skeleton } from "primereact/skeleton";
import Error from "../../components/common/Error";
import CustomInputText from "../../components/forms/CustomInputText";
import { Card } from "primereact/card";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import CustomCheckbox from "../../components/forms/CustomCheckbox";
import { PlanRequest } from "../../models/dtos";
import CustomDropdown from "../../components/forms/CustomDropdown";
import { specialitiesApi } from "../../services/nomenclatures/specialitiesApi";
import { academicYearsApi } from "../../services/nomenclatures/academicYearsApi";
import { degreesApi } from "../../services/nomenclatures/degreesApi";
import { specialityModesApi } from "../../services/nomenclatures/specialityModesApi";
import { planTypesApi } from "../../services/nomenclatures/planTypesApi";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import PlanSubjectsList from "./PlanSubjectsList";
import useFileDownload from "../../hooks/useFileDownload";

const PlanDetails = () => {
  const { id } = useParams();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const {
    data: plan,
    error: planError,
    isLoading: planLoading,
  } = plansApi.useGetPlanWithSubjectsByIdQuery(parseInt(id!), {
    skip: !id,
  });

  const [updatePlan, { isLoading: isUpdatePlanLoading, error: updatePlanError, isSuccess: isUpdatePlanSuccess }] =
    plansApi.useUpdatePlanMutation();

  const [triggerExportPlan] = plansApi.useLazyExportPlanQuery();
  const { downloadFile } = useFileDownload(triggerExportPlan);

  const { data: specialities } = specialitiesApi.useGetSpecialitiesQuery();
  const { data: academicYears } = academicYearsApi.useGetAcademicYearsQuery();
  const { data: degrees } = degreesApi.useGetDegreesQuery();
  const { data: specialityModes } = specialityModesApi.useGetSpecialityModesQuery();
  const { data: planTypes } = planTypesApi.useGetPlanTypesQuery();

  const formik = useFormik({
    initialValues: {
      ...plan?.data,
      isActive: plan?.data?.isActive ?? true,
    } as Partial<PlanRequest>,
    enableReinitialize: true,
    onSubmit: (values) => {
      updatePlan(values)
        .unwrap()
        .then((result) => {
          if (result && "data" in result) {
            navigate(`/plans/${result.data.planId}`);
          }
        });
    },
  });

  const handleExportPlan = () => {
    downloadFile(parseInt(id!));
  };

  useEffect(() => {
    if (isUpdatePlanSuccess) {
      toast.current?.show({ severity: "success", summary: "Обновяване", detail: "Учебният план е обновен." });
    }
  }, [isUpdatePlanSuccess]);

  if (planLoading) return <Skeleton className="w-full h-2rem" />;
  if (planError) return <Error error={planError} />;
  if (isUpdatePlanLoading) return <ProgressSpinner />;
  if (updatePlanError) return <Error error={updatePlanError} />;

  return (
    <FormikProvider value={formik}>
      <div>
        <Toast ref={toast} />
        <form onSubmit={formik.handleSubmit}>
          <Card
            title={`Учебен план / ${plan?.data.specialityName}`}
            footer={
              <div className="flex justify-content-center gap-2">
                <Button type="submit" label="Запази" icon="pi pi-save" />
                <Button
                  type="button"
                  label="Експорт"
                  icon="pi pi-file-excel"
                  severity="secondary"
                  onClick={handleExportPlan}
                />
              </div>
            }
            className="mb-4 shadow-4"
          >
            <div className="grid">
              <div className="col-12 md:col-4">
                <CustomInputText id="code" label="Код" />
              </div>
              <div className="col-12 md:col-4">
                <CustomDropdown
                  id="specialityId"
                  label="Специалност"
                  options={specialities?.data || []}
                  optionLabel="name"
                  optionValue="specialityId"
                  filter
                />
              </div>
              <div className="col-12 md:col-4">
                <CustomDropdown
                  id="degreeId"
                  label="Степен"
                  options={degrees?.data || []}
                  optionLabel="name"
                  optionValue="degreeId"
                />
              </div>
              <div className="col-12 md:col-4">
                <CustomDropdown
                  id="specialityModeId"
                  label="Форма на обучение"
                  options={specialityModes?.data || []}
                  optionLabel="name"
                  optionValue="specialityModeId"
                />
              </div>
              <div className="col-12 md:col-4">
                <CustomDropdown
                  id="planTypeId"
                  label="Тип на плана"
                  options={planTypes?.data || []}
                  optionLabel="name"
                  optionValue="planTypeId"
                />
              </div>
              <div className="col-12 md:col-4">
                <CustomDropdown
                  id="startAcademicYearId"
                  label="Начална академична година"
                  options={academicYears?.data || []}
                  optionLabel="name"
                  optionValue="academicYearId"
                />
              </div>
              <div className="col-12 md:col-4">
                <CustomDropdown
                  id="endAcademicYearId"
                  label="Крайна академична година"
                  options={academicYears?.data || []}
                  optionLabel="name"
                  optionValue="academicYearId"
                />
              </div>
              <div className="col-12 md:col-4">
                <CustomDatepicker id="approvalDate" label="Дата на приемане" />
              </div>
              <div className="col-12 md:col-4">
                <CustomCheckbox id="isActive" label="Активен" />
              </div>
            </div>
          </Card>
        </form>

        {plan?.data?.planSubjects && <PlanSubjectsList plan={plan?.data} />}
      </div>
    </FormikProvider>
  );
};

export default PlanDetails;
